/**
 * E2E encryption primitives -- Web Crypto API (X25519 ECDH + AES-256-GCM).
 * Wire-format compatible with E2EEncryptionService.swift on iOS/macOS.
 *
 * Message wire format:  base64(nonce[12] || ciphertext || tag[16])
 * Key wrap wire format: base64(ephemeralPubKey[32] || nonce[12] || ciphertext[32] || tag[16])
 * Key derivation:       HKDF-SHA256(ecdh_shared_secret, salt=chatId, info="quantis-chat-key-v1")
 */

const WRAP_INFO = new TextEncoder().encode('quantis-chat-key-v1');

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

// base64url (JWK 'x'/'d' fields) -> standard base64 with padding
function b64urlToB64(b64url: string): string {
  const pad = (4 - (b64url.length % 4)) % 4;
  return b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(pad);
}

export async function generateKeyPair(): Promise<CryptoKeyPair> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (crypto.subtle as any).generateKey({ name: 'X25519' }, true, ['deriveBits']);
}

export async function exportPrivateKeyJwk(privateKey: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey('jwk', privateKey);
}

export async function importPrivateKeyFromJwk(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'X25519' }, true, ['deriveBits']);
}

/** Extracts the 32-byte public key from the private key's JWK and returns standard base64. */
export async function publicKeyBase64FromPrivateKey(privateKey: CryptoKey): Promise<string> {
  const jwk = await crypto.subtle.exportKey('jwk', privateKey);
  if (!jwk.x) throw new Error('No x field in X25519 private key JWK');
  return toBase64(fromBase64(b64urlToB64(jwk.x)));
}

export async function importPublicKey(base64: string): Promise<CryptoKey> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (crypto.subtle as any).importKey('raw', fromBase64(base64), { name: 'X25519' }, true, []);
}

async function deriveWrappingKey(
  myPrivateKey: CryptoKey,
  theirPublicKey: CryptoKey,
  chatId: string,
): Promise<CryptoKey> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sharedBits = await crypto.subtle.deriveBits({ name: 'X25519', public: theirPublicKey } as any, myPrivateKey, 256);
  const hkdfKey = await crypto.subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new TextEncoder().encode(chatId), info: WRAP_INFO },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function generateChatKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/**
 * Wraps a chat key for a recipient using ECDH + HKDF + AES-GCM.
 * Returns base64(ephemeralPubKey[32] || nonce[12] || ciphertext[32] || tag[16]).
 */
export async function wrapChatKey(
  chatKey: CryptoKey,
  recipientPublicKeyBase64: string,
  chatId: string,
): Promise<string> {
  const ephemeralPair = await generateKeyPair();
  const recipientPubKey = await importPublicKey(recipientPublicKeyBase64);
  const wrappingKey = await deriveWrappingKey(ephemeralPair.privateKey, recipientPubKey, chatId);

  const chatKeyRaw = await crypto.subtle.exportKey('raw', chatKey);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, wrappingKey, chatKeyRaw),
  ); // ciphertext(32) + tag(16) = 48 bytes

  const ephemeralPubRaw = new Uint8Array(await crypto.subtle.exportKey('raw', ephemeralPair.publicKey));
  const combined = new Uint8Array(32 + 12 + ciphertext.byteLength);
  combined.set(ephemeralPubRaw, 0);
  combined.set(nonce, 32);
  combined.set(ciphertext, 44);
  return toBase64(combined);
}

/**
 * Unwraps a chat key using the local user's private key.
 * Input: base64(ephemeralPubKey[32] || nonce[12] || ciphertext[32] || tag[16]).
 */
export async function unwrapChatKey(
  wrappedKeyBase64: string,
  myPrivateKey: CryptoKey,
  chatId: string,
): Promise<CryptoKey> {
  const wrapped = fromBase64(wrappedKeyBase64);
  if (wrapped.length < 44) throw new Error('Invalid wrapped key length');

  const ephemeralPubKey = await importPublicKey(toBase64(wrapped.slice(0, 32)));
  const nonce = wrapped.slice(32, 44);
  const ciphertext = wrapped.slice(44); // ciphertext(32) + tag(16)

  const wrappingKey = await deriveWrappingKey(myPrivateKey, ephemeralPubKey, chatId);
  const chatKeyRaw = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, wrappingKey, ciphertext);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (crypto.subtle as any).importKey('raw', chatKeyRaw, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Encrypts a plaintext string. Returns base64(nonce[12] || ciphertext || tag[16]). */
export async function encryptMessage(content: string, chatKey: CryptoKey): Promise<string> {
  const data = new TextEncoder().encode(content);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, chatKey, data),
  );
  const combined = new Uint8Array(12 + ciphertext.byteLength);
  combined.set(nonce, 0);
  combined.set(ciphertext, 12);
  return toBase64(combined);
}

/** Decrypts a base64(nonce[12] || ciphertext || tag[16]) string. */
export async function decryptMessage(encryptedBase64: string, chatKey: CryptoKey): Promise<string> {
  const combined = fromBase64(encryptedBase64);
  if (combined.length < 28) throw new Error('Ciphertext too short');
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: combined.slice(0, 12) },
    chatKey,
    combined.slice(12),
  );
  return new TextDecoder().decode(plaintext);
}
